"use client";

import { useEffect, type ReactNode } from "react";

import { trackEvent } from "../analytics";
import { setActiveExperimentProps } from "./active-props";

interface ExperimentsProviderProps {
  readonly assignments: Partial<Record<string, string>>;
  readonly children: ReactNode;
}

const enrolledKey = (experiment: string, variant: string): string =>
  `sq_exp_enrolled:${experiment}:${variant}`;

/** True once we've marked this enrollment as reported for the session. Guards
 *  against double-firing across reloads/navigations; SSR/no-storage = always
 *  fire (best-effort). */
const alreadyEnrolled = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const markEnrolled = (key: string): void => {
  try {
    sessionStorage.setItem(key, "1");
  } catch {}
};

/**
 * Registers the server-computed experiment assignments into the analytics
 * side-channel (so every subsequent event carries exp_<key>=<variant>) and
 * fires a one-time-per-session "Experiment Enrolled" event per experiment.
 */
export function ExperimentsProvider({ assignments, children }: ExperimentsProviderProps) {
  useEffect(() => {
    setActiveExperimentProps(assignments);

    Object.entries(assignments)
      .filter((entry): entry is [string, string] => entry[1] != null)
      .forEach(([experiment, variant]) => {
        const key = enrolledKey(experiment, variant);
        if (alreadyEnrolled(key)) return;
        markEnrolled(key);
        trackEvent("Experiment Enrolled", { experiment, variant }, { interactive: false });
      });
  }, [assignments]);

  return children;
}
